import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  isValidPhoneNumber,
  parsePhoneNumber,
  PhoneNumber,
} from 'react-phone-number-input'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import {
  CountryAndPhone,
  PhoneNumberCountry,
} from '@/components/phone-number-country'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import countries from '@/core/constants/countries.json'

import { RemoveStaffModal } from './RemoveStaffModal'
import { Staff, STAFF_PERMISSION_OPTIONS, StaffPermission } from './types'
import translate from '@/core/services/translate'
import { getTranslation } from '@/account/services/auth'
import { CountryData } from 'country-codes-list'

interface CreateStaffModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  staff?: Staff | null
  onSubmit: (values: IStaffFormValues, id?: string) => Promise<void>
  onDelete?: (id: string) => void
}

export interface IStaffFormValues {
  name: string
  phone: CountryAndPhone
  permissions: StaffPermission[]
}

export const CreateStaffModal = ({
  open,
  onOpenChange,
  staff,
  onSubmit,
  onDelete,
}: CreateStaffModalProps) => {
  const isEditMode = !!staff
  const [removeOpen, setRemoveOpen] = useState(false)

  const language = getTranslation()

  const schema = yup.object().shape({
    name: yup.string().required('Name is required'),
    phone: yup.object().shape({
      countryCode: yup.string().required(),
      countryCodeValue: yup.string().required('Please Select Country!'),
      phoneNumber: yup
        .string()
        .when('countryCallingCode', (countryCallingCode) => {
          if (countryCallingCode) {
            return yup
              .string()
              .test(
                'Valid phone number',
                translate(language.value, 'common.validPhoneNumber'),
                (value, context) => {
                  const country: CountryData = context.parent.countryCodeValue
                  if (value) return isValidPhoneNumber(`${country}${value}`)
                  return false
                },
              )
          } else {
            return yup.string().required('Please Select Country!')
          }
        })
        .required(),
    }),
    permissions: yup.array().of(yup.string().required()).default([]),
  })

  const defaultValues: IStaffFormValues = {
    name: '',
    phone: { countryCode: '', countryCodeValue: '', phoneNumber: '' },
    permissions: [],
  }

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    setValue,
  } = useForm<IStaffFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (staff) {
      setValue('name', staff.name, { shouldValidate: true })
      setValue('permissions', staff.permissions || [], { shouldValidate: true })

      let phone: CountryAndPhone = {
        countryCode: '',
        countryCodeValue: '',
        phoneNumber: staff.phone,
      }
      try {
        const parsed = parsePhoneNumber(staff.phone.replace(/\s+/g, ''))
        if (parsed) {
          const country = countries.find((c) => c.code === parsed.country)
          phone = {
            countryCode: parsed.country || '',
            countryCodeValue: country?.phonePrefix || '',
            phoneNumber: parsed.nationalNumber,
          }
        }
      } catch {
        // fallback to raw phone string
      }
      setValue('phone', phone, { shouldValidate: true })
    }
    return () => {
      reset(defaultValues)
    }
  }, [open, staff])

  const handleClose = () => onOpenChange(false)

  const submit = async (values: IStaffFormValues) => {
    await onSubmit(values, staff?.id)
    handleClose()
  }

  const handleDelete = () => {
    if (!staff || !onDelete) return
    onDelete(staff.id)
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="profile-2user"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Staff</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode ? (
              <Button
                onClick={() => setRemoveOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete Staff
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isSubmitting ||
                  (isEditMode && Object.keys(dirtyFields).length === 0)
                }
                loading={isSubmitting}
                form="create-staff-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-staff-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-6"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneNumberCountry
                buttonClassName="border border-monochrome-120 !rounded-[16px] h-[44px] items-center px-4 pb-0 text-monochrome-2 bg-monochrome-3-60"
                textInputProps={{
                  outline: true,
                  className: '!bg-monochrome-3-60 !rounded-[16px] h-[44px]',
                  withClear: false,
                  prefixClassName: 'left-2 text-monochrome-1',
                  error: errors.phone?.countryCodeValue
                    ? errors.phone.countryCodeValue.message
                    : errors.phone?.phoneNumber?.message,
                  showError: true,
                  prefixAlwaysVisible: true,
                }}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />

          <div className="border-t border-monochrome-120 pt-6">
            <div className="text-2xl font-bold leading-none text-monochrome-1 mb-4">
              Permissions
            </div>

            <div className="flex flex-col gap-3">
              <Controller
                name="permissions"
                control={control}
                render={({ field }) => (
                  <>
                    {STAFF_PERMISSION_OPTIONS.map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <Checkbox
                          checked={field.value.includes(value)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...field.value, value]
                              : field.value.filter((v) => v !== value)
                            field.onChange(next)
                          }}
                        />
                        <span className="text-body-medium text-monochrome-1">
                          {label}
                        </span>
                      </label>
                    ))}
                  </>
                )}
              />
            </div>
          </div>
        </form>
      </Modal>

      <RemoveStaffModal
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        onConfirm={handleDelete}
        staffName={staff?.name}
      />
    </>
  )
}
